import React from "react";

// Ürün kota kartı renkleri — Cihazlar sekmesindeki `quotaColors` ile birebir
// aynı sıra, böylece sekmeler arasında aynı ürün aynı rengi alır.
const QUOTA_CARD_COLORS = [
  { bg: "bg-blue-50 border-blue-200", text: "text-blue-700", label: "text-blue-500", undist: "text-blue-400" },
  { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", label: "text-emerald-500", undist: "text-emerald-400" },
  { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", label: "text-amber-500", undist: "text-amber-400" },
  { bg: "bg-indigo-50 border-indigo-200", text: "text-indigo-700", label: "text-indigo-500", undist: "text-indigo-400" },
  { bg: "bg-pink-50 border-pink-200", text: "text-pink-700", label: "text-pink-500", undist: "text-pink-400" },
  { bg: "bg-purple-50 border-purple-200", text: "text-purple-700", label: "text-purple-500", undist: "text-purple-400" },
];

const fmt = (value) =>
  value === null || value === undefined ? "-" : Number(value).toLocaleString("tr-TR");

/**
 * Liste sekmelerinin başlığındaki ürün başına kota kartları:
 *   Ürün Adı
 *   <sol> | <sağ>
 *
 * Sol = bu firmanın kendisinde (havuz + kendi cihazları), sağ = listedeki
 * firmalarda (havuz + cihaz). Etiketler sekmeye göre verilir — "Bayide |
 * Müşterilerde", "Bayide | Alt Bayilerde", "Firmada | Alt Firmalarda"
 * (kullanıcı kararı 2026-10-09, #300: "Dağıtılan" dili kullanılmaz).
 *
 * @param {Array<{productName, left, right}>} items  left/right null → "-"
 */
const QuotaSplitCards = ({ items = [], loading = false, leftLabel, rightLabel }) => {
  if (!loading && items.length === 0) return null;

  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
      {loading
        ? Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="shrink-0 flex items-center gap-2.5 bg-gray-50 rounded-xl px-4 py-2 animate-pulse"
            >
              <div className="w-1.5 h-10 bg-gray-200 rounded-full" />
              <div>
                <div className="h-3 w-16 bg-gray-200 rounded mb-1.5" />
                <div className="h-5 w-20 bg-gray-200 rounded" />
              </div>
            </div>
          ))
        : items.map((item, index) => {
            const color = QUOTA_CARD_COLORS[index % QUOTA_CARD_COLORS.length];
            return (
              <div
                key={item.productName || index}
                className={`shrink-0 ${color.bg} border rounded-xl px-4 py-2.5 transition-all hover:shadow-sm`}
              >
                <p className={`text-xs font-Bold ${color.label} whitespace-nowrap leading-none mb-1.5`}>
                  {item.productName}
                </p>
                <div className="flex items-baseline gap-2">
                  <div className="text-center">
                    <p className={`text-lg font-Bold ${color.text} leading-none tabular-nums`}>
                      {fmt(item.left)}
                    </p>
                    <p className="text-[9px] font-Medium text-gray-400 mt-0.5">{leftLabel}</p>
                  </div>
                  <span className="text-gray-300 font-light text-lg leading-none">|</span>
                  <div className="text-center">
                    <p className={`text-lg font-Bold ${color.undist} leading-none tabular-nums`}>
                      {fmt(item.right)}
                    </p>
                    <p className="text-[9px] font-Medium text-gray-400 mt-0.5">{rightLabel}</p>
                  </div>
                </div>
              </div>
            );
          })}
    </div>
  );
};

/**
 * Listedeki firmaların ürün başına TOPLAM kotası (havuz + cihazlar).
 * `poolQuota` = firma havuzu, `quota` = firmanın cihazlarındaki toplam
 * (dağıtımı açık + kapalı; bkz. Server firm-quota.js).
 */
export const sumFirmsQuotaByProduct = (firms = []) => {
  const totals = new Map();
  firms.forEach((f) => {
    const list = Array.isArray(f.productInfo?.productInfo) ? f.productInfo.productInfo : [];
    list.forEach((p) => {
      const sum = (Number(p.poolQuota) || 0) + (Number(p.quota) || 0);
      totals.set(p.productName, (totals.get(p.productName) || 0) + sum);
    });
  });
  return totals;
};

/**
 * Satırdaki firmanın ürün başına kota özeti — SubFirmQuotaDrawer'ın "Kota
 * Geri Al" yönünde KAYNAK firma özeti olarak beklediği biçim.
 * `poolQuota` havuz, `quota` cihazlardaki toplam (Server firm-quota.js).
 * @returns {Array<{productName, available, deviceQuota, total, quotaDistribution}>}
 */
export const firmProductQuotaSummary = (firm) => {
  const items = firm?.productInfo?.productInfo;
  if (!Array.isArray(items)) return [];
  return items
    .filter((p) => p.quotaDistribution !== false)
    .map((p) => {
      const available = Number(p.poolQuota) || 0;
      const deviceQuota = Number(p.quota) || 0;
      return {
        productName: p.productName,
        available,
        deviceQuota,
        total: available + deviceQuota,
        quotaDistribution: p.quotaDistribution,
      };
    });
};

export default QuotaSplitCards;
